import { useEffect, useMemo, useRef, useState } from 'react';

/** How long typing pauses before a *live* search is sent (only for a truncated tenant). */
const DEBOUNCE_MS = 250;
/** Fewer characters than this and no live search is sent. Local matching has no minimum. */
const LIVE_MIN_CHARS = 2;
/** Rows shown at once; the rest are reached by typing more. */
const MAX_SHOWN = 50;

/** Case-insensitive "contains" across the fields a person would recognise a user by. */
export function matches(user, term) {
  const needle = term.toLowerCase();
  return [user.displayName, user.userPrincipalName, user.mail, user.jobTitle].some(
    (text) => typeof text === 'string' && text.toLowerCase().includes(needle),
  );
}

/**
 * A username box backed by the tenant directory. When the form opens it loads the
 * tenant's users once and holds them, so typing matches instantly against what it
 * already has, and a caret button opens the whole list to scroll and click. It still
 * accepts whatever is typed: the value is always the username, picked or typed.
 *
 * A tenant too large to list in full comes back `truncated`; for those the picker also
 * searches live for what is typed, after a short pause, and merges the answers in.
 *
 * A WAI-ARIA combobox: the input is the combobox, the list the listbox; arrow keys
 * move, Enter picks, Escape closes. No library, no inline style — it lives under the
 * strict CSP like everything else. With `enabled` false it is a plain email box plus a
 * one-line note, and makes no calls at all.
 */
export function DirectoryUserPicker({
  id,
  value,
  onChange,
  onBlur,
  onPick,
  directory,
  enabled,
  disabled,
  placeholder,
  describedBy,
  invalid,
}) {
  const [listing, setListing] = useState(undefined);
  const [loadState, setLoadState] = useState(enabled ? 'loading' : 'idle');
  // Live answers, remembered with the term they answer, so a stale answer for an
  // earlier term is simply not used rather than having to be cleared.
  const [live, setLive] = useState({ term: '', users: [] });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  // The value the last pick wrote. While the box still says that, there is nothing
  // to match and the list stays closed unless the caret opens it. State, not a ref: it
  // decides what renders.
  const [picked, setPicked] = useState(undefined);
  const liveRequest = useRef(0);

  const listboxId = `${id}-listbox`;
  const noteId = `${id}-note`;
  const term = (value ?? '').trim();
  const typedSomething = term !== '' && term !== picked;

  // Load the tenant once, when the form opens.
  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    directory
      .list({ signal: controller.signal })
      .then((loaded) => {
        setListing(loaded);
        setLoadState('idle');
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        setListing({ users: [], truncated: true });
        setLoadState('error');
      });
    return () => controller.abort();
  }, [enabled, directory]);

  // Live search, only for what the listing could not hold.
  const liveWanted =
    enabled && Boolean(listing?.truncated) && typedSomething && term.length >= LIVE_MIN_CHARS;
  useEffect(() => {
    if (!liveWanted) return undefined;
    const requestId = ++liveRequest.current;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const users = await directory.search(term, { signal: controller.signal });
        if (requestId === liveRequest.current) setLive({ term, users });
      } catch (error) {
        if (requestId === liveRequest.current && error?.name !== 'AbortError') {
          setLive({ term, users: [] });
        }
      }
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [liveWanted, term, directory]);

  // What the list shows: the held users that match, then live answers not already in it.
  const results = useMemo(() => {
    if (!listing) return [];
    const local = typedSomething
      ? listing.users.filter((user) => matches(user, term))
      : listing.users;
    const extra = liveWanted && live.term === term ? live.users : [];
    const seen = new Set(local.map((user) => user.userPrincipalName));
    return [...local, ...extra.filter((user) => !seen.has(user.userPrincipalName))].slice(
      0,
      MAX_SHOWN,
    );
  }, [listing, live, liveWanted, typedSomething, term]);

  const showList = open && results.length > 0;
  // Results can shrink underneath the highlighted row; never point past the end.
  const activeIndex = Math.min(active, Math.max(results.length - 1, 0));

  function pick(user) {
    setPicked(user.userPrincipalName);
    onChange(user.userPrincipalName);
    setOpen(false);
    setActive(0);
    onPick?.(user);
  }

  function handleChange(event) {
    const next = event.target.value;
    if (next !== picked) setPicked(undefined);
    onChange(next);
    // Typing opens the list at its first row; clearing the box closes it.
    setActive(0);
    setOpen(next.trim() !== '' && next !== picked);
  }

  function handleKeyDown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!showList) setOpen(true);
        else setActive((activeIndex + 1) % results.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (showList) setActive((activeIndex - 1 + results.length) % results.length);
        break;
      case 'Enter':
        if (showList && results[activeIndex]) {
          event.preventDefault();
          pick(results[activeIndex]);
        }
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          setOpen(false);
        }
        break;
      default:
    }
  }

  function handleBlur(event) {
    setOpen(false);
    onBlur?.(event);
  }

  const common = {
    id,
    type: 'email',
    value: value ?? '',
    onChange: handleChange,
    onBlur: handleBlur,
    disabled,
    placeholder,
    autoComplete: 'off',
    'aria-invalid': invalid ? true : undefined,
  };

  if (!enabled) {
    return (
      <>
        <input {...common} aria-describedby={[describedBy, noteId].filter(Boolean).join(' ')} />
        <p id={noteId} className="help">
          Directory search is not available; type the username in full.
        </p>
      </>
    );
  }

  const held = listing ? listing.users.length : 0;

  return (
    <div className="combobox">
      <div className="combobox-row">
        <input
          {...common}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showList}
          aria-controls={listboxId}
          aria-activedescendant={showList ? `${id}-option-${activeIndex}` : undefined}
          aria-describedby={describedBy}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className="combobox-toggle"
          aria-label={showList ? 'Hide the list of users' : 'Show all users'}
          aria-expanded={showList}
          aria-controls={listboxId}
          disabled={disabled || held === 0}
          // Keep focus in the box so Escape and the arrows keep working.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            setOpen((was) => !was);
            document.getElementById(id)?.focus();
          }}
        >
          ▾
        </button>
      </div>
      <ul id={listboxId} role="listbox" className="combobox-list" hidden={!showList}>
        {results.map((user, index) => (
          <li
            key={user.userPrincipalName}
            id={`${id}-option-${index}`}
            role="option"
            aria-selected={index === activeIndex}
            className="combobox-option"
            // Prevent the input losing focus before the click lands.
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={() => setActive(index)}
            onClick={() => pick(user)}
          >
            <span className="combobox-name">{user.displayName}</span>
            <span className="combobox-upn">{user.userPrincipalName}</span>
            {user.jobTitle && <span className="combobox-title">{user.jobTitle}</span>}
          </li>
        ))}
      </ul>
      {showList && held > MAX_SHOWN && results.length === MAX_SHOWN && (
        <p className="help">Showing the first {MAX_SHOWN}; keep typing to narrow the list.</p>
      )}
      {loadState === 'loading' && (
        <p className="help" role="status">
          Loading the directory…
        </p>
      )}
      {loadState === 'error' && (
        <p className="help" role="status">
          The directory could not be loaded; you can still type the username in full.
        </p>
      )}
    </div>
  );
}
